import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { useSnackBars } from '@swyftx/aviary/complex/GlobalSnackBars/useSnackBars';

import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { useSwyftxMarginStore } from 'src/lib/margin';
import {
  useFetchMarginTransactionReportsJob,
  useDownloadMarginTransactionReport,
  useStartMarginTransactionReportJob,
} from 'src/lib/margin/hooks/transaction-reports';

type Props = {
  isValid?: boolean;
  startDate?: DateTime;
  endDate?: DateTime;
  timezone?: string;
};

const MarginTransactionsReportDownloadButton: React.FC<Props> = ({ isValid, startDate, endDate, timezone }) => {
  const { t } = useTranslation('margin');
  const { addSnackBar } = useSnackBars();
  const { traderAccountId } = useSwyftxMarginStore();
  const [jobUuid, setJobUuid] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [downloaded, setDownloaded] = useState<boolean>(false);

  const { data } = useFetchMarginTransactionReportsJob({ jobUuid });
  const downloadTransactionReport = useDownloadMarginTransactionReport();
  const startJob = useStartMarginTransactionReportJob();

  const onGenerateReport = useCallback(async () => {
    if (!traderAccountId || !startDate || !endDate || !timezone) return;

    try {
      setLoading(true);
      const resp = await startJob.mutateAsync({
        traderAccountId,
        startDate: startDate.toMillis(),
        endDate: endDate.toMillis(),
        timezone,
      });

      setJobUuid(resp.data.jobUuid);
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: t('transactionReports.errors.failed'),
        showIcon: true,
        duration: 5_000,
      });
    }
  }, [addSnackBar, endDate, startDate, startJob, t, timezone, traderAccountId]);

  const onDownloadReport = useCallback(async () => {
    if (!jobUuid || !startDate || !endDate) return;

    try {
      setDownloaded(true);
      const resp = await downloadTransactionReport.mutateAsync({ jobUuid });
      const blob = new Blob([resp.data], { type: 'text/csv' });
      FileSaver.saveAs(blob, `swyftx_derivatives_${startDate.toISODate()}_${endDate.toISODate()}.csv`);
    } catch (e) {
      addSnackBar({
        variant: 'error',
        content: t('transactionReports.errors.failed'),
        showIcon: true,
        duration: 5_000,
      });
    } finally {
      setLoading(false);
      setJobUuid(undefined);
      setDownloaded(false);
    }
  }, [addSnackBar, downloadTransactionReport, endDate, jobUuid, startDate, t]);

  useEffect(() => {
    if (data?.status === 'COMPLETED' && data.jobUuid === jobUuid && !downloaded) onDownloadReport();
  }, [data, downloaded, jobUuid, onDownloadReport]);

  return (
    <Button
      variant='filled'
      size='lg'
      color='primary'
      disabled={!isValid || loading}
      onClick={onGenerateReport}
      loading={loading}
    >
      {t('transactionReports.downloadReport')}
    </Button>
  );
};

export { MarginTransactionsReportDownloadButton };
